import React, { useState, useEffect } from "react";
import { Fade } from "react-reveal";
import "./Slideshow.css";

const Slideshow = ({ theme = {} }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [imageLoaded, setImageLoaded] = useState({});
  const [slides, setSlides] = useState([]);

  // Load images dynamically from the manifest file
  useEffect(() => {
    const loadSlides = async () => {
      try {
        // Use process.env.PUBLIC_URL to get the correct base path (handles /church/ subpath)
        const basePath = process.env.PUBLIC_URL || "";
        const manifestPath = `${basePath}/images/slideshow/manifest.json`;

        const response = await fetch(manifestPath);
        if (!response.ok) {
          throw new Error(`Failed to fetch manifest: ${response.status}`);
        }

        const data = await response.json();

        // Create slide objects from the manifest with correct base path
        const loadedSlides = data.images.map((fileName, index) => ({
          id: index,
          src: `${basePath}/images/slideshow/${fileName}`,
          alt: `Church Photo`,
        }));

        setSlides(loadedSlides);
      } catch (error) {
        console.error("Error loading slides:", error);
        setSlides([]); // Empty array if manifest not found
      }
    };

    loadSlides();
  }, []);

  useEffect(() => {
    if (!autoPlay) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [autoPlay, slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setAutoPlay(false);
    // Resume autoplay after 10 seconds
    setTimeout(() => setAutoPlay(true), 10000);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setAutoPlay(false);
    setTimeout(() => setAutoPlay(true), 10000);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setAutoPlay(false);
    setTimeout(() => setAutoPlay(true), 10000);
  };

  return (
    <div
      className="slideshow-container"
      style={{ backgroundColor: theme.body }}
    >
      <Fade duration={1000}>
        <div className="slides-wrapper">
          {/* Main Slide */}
          <div className="slide-main">
            {slides.map((slide, index) => (
              <div
                key={index}
                className={`slide ${index === currentSlide ? "active" : ""}`}
                style={
                  index === currentSlide
                    ? { animation: "slideIn 0.8s ease-in-out forwards" }
                    : index ===
                      (currentSlide - 1 + slides.length) % slides.length
                    ? { animation: "slideOut 0.8s ease-in-out forwards" }
                    : {}
                }
              >
                <img
                  src={slide.src}
                  alt={slide.alt}
                  onLoad={() =>
                    setImageLoaded({ ...imageLoaded, [index]: true })
                  }
                  onError={() =>
                    console.error(`Failed to load slide ${index}: ${slide.src}`)
                  }
                />
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            className="slide-arrow left-arrow"
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            &#10094;
          </button>
          <button
            className="slide-arrow right-arrow"
            onClick={nextSlide}
            aria-label="Next slide"
          >
            &#10095;
          </button>

          {/* Slide Indicators/Dots */}
          <div className="slide-indicators">
            {slides.map((_, index) => (
              <span
                key={index}
                className={`dot ${index === currentSlide ? "active" : ""}`}
                onClick={() => goToSlide(index)}
                style={{
                  backgroundColor:
                    index === currentSlide
                      ? theme.highlight
                      : "rgba(255,255,255,0.5)",
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Slide Counter */}
          <div className="slide-counter" style={{ color: theme.text }}>
            {currentSlide + 1} / {slides.length}
          </div>
        </div>
      </Fade>
    </div>
  );
};

export default Slideshow;
